import logoDefault from "~/assets/images/logo.png";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { CustomImageType, FloorImageType, styleModes, feedbackModes } from "./Commons";
import { atomWithDynamoDB, setCustomerIdProvider } from "./utils/atomWithDynamoDBUtil";
import { Amplify } from "aws-amplify";
import { getCurrentUser } from "aws-amplify/auth";
import { getDefaultStore } from "jotai";
import awsconfig from "~/aws-exports";

// Amplify設定を初期化（既に初期化されていても安全）
Amplify.configure(awsconfig);

// 場所情報の型定義
export type locationListType = {
  enName: string;
  jaName: string;
  lat: number;
  lon: number;
  address: string;
};

// デフォルトの場所情報
export const locationList: locationListType = {
  enName: process.env.REACT_APP_LOCATION_EN_NAME || "",
  jaName: process.env.REACT_APP_LOCATION_JA_NAME || "",
  lat: parseFloat(process.env.REACT_APP_LOCATION_LATITUDE || "0"),
  lon: parseFloat(process.env.REACT_APP_LOCATION_LONGITUDE || "0"),
  address: process.env.REACT_APP_LOCATION_ADDRESS || "",
};

/**
 * 環境に応じたAtom定義
 *
 * 保存先の使い分け:
 * - atomWithDynamoDB:
 *   - Web管理画面で実行時はDynamoDBのみに保存（複数端末で共有）
 *   - チャット画面で実行時はlocalStorageのみに保存（端末固有設定）
 */

// Cognitoから顧客IDを取得する関数
export const getCustomerId = async (): Promise<string> => {
  // オンプレミス環境の場合は空文字を返す
  if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem") {
    return "";
  }

  try {
    const currentUser = await getCurrentUser();
    return currentUser.userId;
  } catch (error) {
    console.error("Failed to get customer ID from Cognito:", error);
    // 認証が失敗した場合はエラーを投げる
    throw new Error("Cognito authentication required. Please sign in to continue.");
  }
};

// 動的な顧客IDを保持するAtom（ローカルストレージに保存）
export const customerIdAtom = atomWithStorage<string | null>("customerId", null);

/**
 * 現在のcustomerIdを取得する（非同期）
 * 認証が必要な場合は適切にエラーハンドリングを行う
 * @returns customerId
 * @throws Error when authentication is required
 */
export const getCurrentCustomerId = async (): Promise<string> => {
  // オンプレミス環境の場合は空文字を返す
  if (process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem") {
    return "";
  }

  // まずAtomから値を取得
  const atomValue = getDefaultStore().get(customerIdAtom);
  if (atomValue) {
    return atomValue;
  }

  // Atomにない場合はCognitoから取得を試行
  try {
    return await getCustomerId();
  } catch (error) {
    console.error("Customer ID authentication failed:", error);
    throw new Error("Authentication required. Please sign in to access VRM features.");
  }
};

/**
 * 現在のcustomerIdを取得する（同期版）
 * Atomからのみ取得し、存在しない場合はnullを返す
 * @returns customerId or null
 */
export const getCurrentCustomerIdSync = (): string | null => {
  return getDefaultStore().get(customerIdAtom);
};

// 認証状態が変わった時にcustomerIdを更新する関数
export const updateCustomerIdFromCognito = async () => {
  try {
    const customerId = await getCustomerId();
    // この関数は外部から呼び出される想定（認証コンテキストなど）
    return customerId;
  } catch (error) {
    console.error("Failed to update customer ID from Cognito:", error);
    throw error;
  }
};

// atomWithDynamoDBUtil にCognitoからのcustomerID取得を設定
setCustomerIdProvider(getCustomerId);

// ToDo SSM運用想定
const CONFIG_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_CONFIG_TABLE_NAME as string;
const CUSTOMER_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_CUSTOMER_TABLE_NAME as string;

// 待機タイマー（秒）
export const waitTimerAtom = atomWithDynamoDB<number>(
  "waitTimer",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  3,
  {
    fromDynamoDB: (item) => (item.waitTimer as number) || 3,
    toDynamoDB: (value) => ({ waitTimer: value }),
  }
);

// 会話モード（0: 通常, 1: 要約, 9: その他）
export const talkingModeAtom = atomWithDynamoDB<number>(
  "talkingMode",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  0,
  {
    fromDynamoDB: (item) => (item.talkingMode as number) || 0,
    toDynamoDB: (value) => ({ talkingMode: value }),
  }
);

// 聞き取りモード（0: 自動送信, 1: ボタンで送信, 2: ボタン押している間聞き取り）
export const listeningModeAtom = atomWithDynamoDB<number>(
  "listeningMode",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  0,
  {
    fromDynamoDB: (item) => (item.listeningMode as number) || 0,
    toDynamoDB: (value) => ({ listeningMode: value }),
  }
);

// 一時返答有効/無効
export const isFirstResponseAtom = atomWithDynamoDB<boolean>(
  "isFirstResponse",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  false,
  {
    fromDynamoDB: (item) => !!item.isFirstResponse,
    toDynamoDB: (value) => ({ isFirstResponse: value }),
  }
);

// 呼びかけ有効/無効
export const isCallOutAtom = atomWithDynamoDB<boolean>(
  "isCallOut",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  false,
  {
    fromDynamoDB: (item) => !!item.isCallOut,
    toDynamoDB: (value) => ({ isCallOut: value }),
  }
);

// 場所情報
export const locationInfoAtom = atomWithDynamoDB<locationListType>(
  "locationInfo",
  CUSTOMER_TABLE_NAME, // 場所情報は顧客テーブルに保存
  customerIdAtom,
  locationList,
  {
    fromDynamoDB: (item) => ({
      jaName: (item.jaName as string) ?? "",
      enName: (item.enName as string) ?? "",
      lat: (item.lat as number | undefined) ?? locationList.lat,
      lon: (item.lon as number | undefined) ?? locationList.lon,
      address: (item.address as string) ?? "",
    }),
    toDynamoDB: (value) => ({
      jaName: value.jaName,
      enName: value.enName,
      lat: value.lat,
      lon: value.lon,
      address: value.address,
    }),
  }
);

// プロンプト
export const promptAtom = atomWithDynamoDB<string>(
  "prompt",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  "",
  {
    // DBカラムはsystemPrompt
    fromDynamoDB: (item) => (item.systemPrompt as string) || "",
    toDynamoDB: (value) => ({ systemPrompt: value }),
  }
);

// ロゴ画像
export const logoSrcAtom = atomWithDynamoDB<string>(
  "customLogo",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  logoDefault,
  {
    fromDynamoDB: (item) => (item.customLogo as string) || logoDefault,
    toDynamoDB: (value) => ({ customLogo: value }),
  }
);

// 二次元コードタイトル
export const qrCodeTitleAtom = atomWithDynamoDB<string>(
  "qrCodeTitle",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  "",
  {
    fromDynamoDB: (item) => (item.qrCodeTitle as string) || "",
    toDynamoDB: (value) => ({ qrCodeTitle: value }),
  }
);

// 二次元コードURL
export const qrCodeUrlAtom = atomWithDynamoDB<string>(
  "qrCodeURL",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  "",
  {
    fromDynamoDB: (item) => (item.qrCodeUrl as string) || "",
    toDynamoDB: (value) => ({ qrCodeUrl: value }),
  }
);

// フロアマップ画像
export const floorImagesAtom = atomWithDynamoDB<FloorImageType[]>(
  "floorImages",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  [],
  {
    fromDynamoDB: (item) => {
      const floorImages = item.floorImages as string[] | undefined;
      if (!floorImages || floorImages.length === 0) return [];

      const result: FloorImageType[] = [];
      for (let i = 0; i < floorImages.length; i += 2) {
        if (i + 1 < floorImages.length) {
          result.push({
            src: floorImages[i], // floorImages/{ファイル名}
            name: floorImages[i + 1], // 表示名
          });
        }
      }
      return result;
    },
    toDynamoDB: (value) => {
      const floorImages: string[] = [];
      value.forEach((floor) => {
        floorImages.push(floor.src); // floorImages/{ファイル名}
        floorImages.push(floor.name); // 表示名
      });
      return { floorImages };
    },
  }
);

// 独り言
export const isGreeterAtom = atomWithDynamoDB<boolean>(
  "isGreeter",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  true,
  {
    fromDynamoDB: (item) => !!item.isGreeter,
    toDynamoDB: (value) => ({ isGreeter: value }),
  }
);

// スタイルモード
export const styleModeAtom = atomWithDynamoDB<string>(
  "styleMode",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  styleModes.formal,
  {
    fromDynamoDB: (item) => (item.style as string) || styleModes.formal,
    toDynamoDB: (value) => ({ style: value }),
  }
);

// カスタム画像
export const customImagesAtom = atomWithDynamoDB<CustomImageType[]>(
  "customImages",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  [],
  {
    fromDynamoDB: (item) => {
      const customImages = item.imagePlaceholders as Record<string, string> | undefined;
      if (!customImages) return [];
      const result: CustomImageType[] = [];

      // オブジェクトのキー・バリューをCustomImageType[]に変換
      Object.entries(customImages).forEach(([name, src]) => {
        result.push({
          key: name,
          src: src,
        });
      });

      return result;
    },
    toDynamoDB: (value) => {
      const imagePlaceholders: Record<string, string> = {};
      value.forEach((image) => {
        imagePlaceholders[image.key] = image.src;
      });
      return { imagePlaceholders };
    },
  }
);

// 統計フィードバックモード（0: シンプルモード、1: 5段階モード）
export const feedbackModeAtom = atomWithDynamoDB<number>(
  "feedbackMode",
  CONFIG_TABLE_NAME,
  customerIdAtom,
  feedbackModes.simple,
  {
    fromDynamoDB: (item) => (item.feedbackMode as number) ?? feedbackModes.simple,
    toDynamoDB: (value) => ({ feedbackMode: value }),
  }
);

// ========================================
// DynamoDB対応Atom一覧
// 新しいAtomを追加した場合は、dynamoDBAtomsにも追加すること
// ========================================

/**
 * DynamoDB対応Atomの初期化完了フラグ
 * すべてのAtomがDynamoDBから初期値を取得完了したかを示す
 */
export const isAtomsInitializedAtom = atom<boolean>(false);

/**
 * DynamoDB対応Atomの配列
 * 新しいAtomを追加した場合は、この配列にも追加すること
 */
export const dynamoDBAtoms = [
  waitTimerAtom,
  talkingModeAtom,
  listeningModeAtom,
  isFirstResponseAtom,
  isCallOutAtom,
  locationInfoAtom,
  promptAtom,
  logoSrcAtom,
  qrCodeTitleAtom,
  qrCodeUrlAtom,
  floorImagesAtom,
  isGreeterAtom,
  styleModeAtom,
  customImagesAtom,
  feedbackModeAtom,
] as const;
